import { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DynamicBlogPost from "@/pages/DynamicBlogPost";

// Map of blog slugs to their components
import AIReceptionistForSmallBusiness from "@/pages/blog/AIReceptionistForSmallBusiness";
import AIForMedicalClinics from "@/pages/blog/AIForMedicalClinics";
import AIForRealEstateAgents from "@/pages/blog/AIForRealEstateAgents";
import AIForRestaurants from "@/pages/blog/AIForRestaurants";
import AIVoiceAgentVsChatbot from "@/pages/blog/AIVoiceAgentVsChatbot";
import AIvsHumanAgentsCost from "@/pages/blog/AIvsHumanAgentsCost";
import AIVoiceCloningPhoneAgent from "@/pages/blog/AIVoiceCloningPhoneAgent";
import BestAIPhoneAgentSoftware from "@/pages/blog/BestAIPhoneAgentSoftware";
import ChatGPTMCPPhoneCalls from "@/pages/blog/ChatGPTMCPPhoneCalls";
import FutureOfAICustomerService from "@/pages/blog/FutureOfAICustomerService";
import HowToSetUpAICallCenter from "@/pages/blog/HowToSetUpAICallCenter";
import IntegrateAIWhatsApp from "@/pages/blog/IntegrateAIWhatsApp";
import ReduceCustomerSupportCosts from "@/pages/blog/ReduceCustomerSupportCosts";
import TopWaysAIPhoneSupport from "@/pages/blog/TopWaysAIPhoneSupport";
import WhatIsAIAnsweringService from "@/pages/blog/WhatIsAIAnsweringService";
import OpenClawPhoneCalls from "@/pages/blog/OpenClawPhoneCalls";

const blogComponents: Record<string, React.ComponentType> = {
  "ai-receptionist-for-small-business": AIReceptionistForSmallBusiness,
  "ai-for-medical-clinics": AIForMedicalClinics,
  "ai-for-real-estate-agents": AIForRealEstateAgents,
  "ai-for-restaurants": AIForRestaurants,
  "ai-voice-agent-vs-chatbot": AIVoiceAgentVsChatbot,
  "ai-vs-human-agents-cost": AIvsHumanAgentsCost,
  "ai-voice-cloning-phone-agent": AIVoiceCloningPhoneAgent,
  "best-ai-phone-agent-software": BestAIPhoneAgentSoftware,
  "chatgpt-mcp-phone-calls": ChatGPTMCPPhoneCalls,
  "future-of-ai-customer-service": FutureOfAICustomerService,
  "how-to-set-up-ai-call-center": HowToSetUpAICallCenter,
  "integrate-ai-whatsapp": IntegrateAIWhatsApp,
  "reduce-customer-support-costs": ReduceCustomerSupportCosts,
  "top-ways-ai-phone-support": TopWaysAIPhoneSupport,
  "what-is-ai-answering-service": WhatIsAIAnsweringService,
  "openclaw-phone-calls": OpenClawPhoneCalls,
};

const supportedLanguages = ["en", "es", "fr", "de", "pt", "ar", "ja", "hi", "zh", "he"];

export function LocalizedBlogWrapper() {
  const { lang, slug } = useParams<{ lang: string; slug: string }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
      localStorage.setItem("i18nextLng", lang);
    }
  }, [lang, i18n]);

  // If language is not supported, redirect to English version
  if (!lang || !supportedLanguages.includes(lang)) {
    return <Navigate to={`/blog/en/${slug}`} replace />;
  }

  if (!slug) {
    return <Navigate to="/blog" replace />;
  }

  // If it's a hardcoded blog, render the component
  if (blogComponents[slug]) {
    const BlogComponent = blogComponents[slug];
    return <BlogComponent />;
  }

  // Otherwise, fall back to dynamic blog post from database
  return <DynamicBlogPost />;
}

export { supportedLanguages, blogComponents };
